import { Link, useLocation } from 'react-router'
import { LangFace } from '../../../../components/site-header/LangFace'
import { HEADER_CIRCLE } from '../../../../components/site-header/sizes'
import { useCopy, useLocale } from '../../content'
import type { Locale } from '../../content/types'
import { altPath } from '../../routes'
import { BAR_COIN } from './ThemeToggle'

/**
 * The link to this page in the other language (`altPath` keeps the visitor on
 * the same route; off-table paths fall back to the other home), drawn as the
 * shared round language button: a 文A glyph and the target language's code,
 * "EN" / "IT", in the header's ink (no flags).
 * The visible code is aria-hidden; the accessible name (and hover title) is
 * the language's own name, "English" / "Italiano", so the link carries that
 * `lang`.
 *
 * `hrefLang` + `rel="alternate"` on a body link are hints only — crawlers read
 * the `<link rel="alternate" hreflang>` set in the head (head.ts), and the
 * prerender check counts only those.
 *
 * `className` REPLACES the round link's colour set (like ThemeToggle's);
 * default: the bar's paper coin. The mobile-menu body passes its own.
 */
export function LangSwitch({ className = BAR_COIN }: { className?: string }) {
  const copy = useCopy()
  const locale = useLocale()
  const { pathname } = useLocation()
  const other: Locale = locale === 'it' ? 'en' : 'it'
  return (
    <nav aria-label={copy.a11y.langNav} className="flex">
      <Link
        to={altPath(pathname, other)}
        hrefLang={other}
        lang={other}
        rel="alternate"
        title={copy.nav.switchTo}
        className={`${HEADER_CIRCLE} ${className} transition-opacity hover:opacity-90`}
      >
        <LangFace lang={other} />
        <span className="sr-only">{copy.nav.switchTo}</span>
      </Link>
    </nav>
  )
}
